import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Roles } from '../auth/rbac'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'

const CATEGORY_LABELS = {
    INSURANCE: '보험',
    ECONOMY: '경제동향',
    REAL_ESTATE: '부동산',
    TAX: '세금',
    SECURITIES: '증권/투자',
    BANKING: '은행/금리',
    FINANCIAL_PRODUCT: '금융상품',
    MANAGEMENT: '경영',
    ETC: '기타'
}

export default function InsuranceNewsNewPage() {
    const navigate = useNavigate()
    const { hasAnyRole } = useAuth()
    const { show } = useToast()
    const [title, setTitle] = useState('')
    const [summary, setSummary] = useState('')
    const [externalUrl, setExternalUrl] = useState('')
    const [category, setCategory] = useState('INSURANCE')
    const [submitting, setSubmitting] = useState(false)

    const isOperator = hasAnyRole && hasAnyRole([Roles.OPERATOR])

    if (!isOperator) {
        return (
            <div className="p-8 text-center">
                <p className="text-red-600">권한이 없습니다.</p>
            </div>
        )
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!title.trim() || !externalUrl.trim() || !category) {
            show('제목, 외부 링크, 카테고리를 모두 입력해주세요', 'error')
            return
        }

        // URL 형식 검증
        try {
            new URL(externalUrl)
        } catch {
            show('올바른 URL 형식이 아닙니다', 'error')
            return
        }

        setSubmitting(true)
        try {
            const res = await api.post('/insurance-news', {
                title: title.trim(),
                summary: summary.trim() || null,
                externalUrl: externalUrl.trim(),
                category
            })
            const body = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const id = body?.id
            if (id) {
                show('작성되었습니다', 'success')
                navigate('/work/insurance-news')
            } else {
                show('작성은 완료되었으나 ID를 확인하지 못했습니다', 'warning')
                navigate('/work/insurance-news')
            }
        } catch (err) {
            const message = err?.response?.data?.message || '작성에 실패했습니다'
            show(message, 'error')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="mx-auto max-w-3xl space-y-6 p-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">보험뉴스 작성</h1>
                <button
                    type="button"
                    onClick={() => navigate('/work/insurance-news')}
                    className="text-sm text-slate-600 hover:text-slate-900"
                >
                    목록으로
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 rounded-lg border border-slate-200 bg-white p-6">
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                        제목 <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="뉴스 제목을 입력하세요"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                        요약 (선택)
                    </label>
                    <textarea
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        placeholder="간단한 요약을 입력하세요 (저작권 문제 없는 요약만)"
                        rows={3}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    />
                    <p className="mt-1 text-xs text-slate-500">저작권 문제가 없는 간단한 요약만 입력해주세요</p>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                        외부 링크 URL <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="url"
                        value={externalUrl}
                        onChange={(e) => setExternalUrl(e.target.value)}
                        placeholder="https://..."
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                        required
                    />
                    <p className="mt-1 text-xs text-slate-500">뉴스 원문 링크를 입력하세요</p>
                </div>

                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                        카테고리 <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                        required
                    >
                        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={() => navigate('/work/insurance-news')}
                        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                        취소
                    </button>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="rounded-lg bg-brand-sky px-4 py-2 text-sm font-semibold text-white hover:bg-brand-sky/90 disabled:opacity-50"
                    >
                        {submitting ? '작성 중...' : '작성'}
                    </button>
                </div>
            </form>
        </div>
    )
}
